import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, ApiError } from '../api/client';
import { cn } from '../utils/cn';
import { ONBOARDING_PATH } from '../utils/onboarding';

const AFTER_EMAIL_VERIFY_KEY = 'afterEmailVerify';

function consumePostVerifyPath(): string {
  try {
    const next = sessionStorage.getItem(AFTER_EMAIL_VERIFY_KEY);
    if (next) {
      sessionStorage.removeItem(AFTER_EMAIL_VERIFY_KEY);
      return next;
    }
  } catch {
    /* ignore */
  }
  return '/dashboard';
}

/** Post-verify destination: `welcome=1` on the link (set by API email) works across tabs; sessionStorage only works same tab as signup. */
function resolvePostVerifyPath(searchParams: URLSearchParams): string {
  const w = searchParams.get('welcome');
  if (w === '1' || w === 'true') {
    try {
      sessionStorage.removeItem(AFTER_EMAIL_VERIFY_KEY);
    } catch {
      /* ignore */
    }
    return ONBOARDING_PATH;
  }
  return consumePostVerifyPath();
}

function isUserEffectivelyVerified(
  user: {
    emailVerified?: boolean;
    isAdmin?: boolean;
  } | null
): boolean {
  if (!user) return false;
  if (user.isAdmin) return true;
  // undefined = legacy stored user without key — treat as verified
  return user.emailVerified !== false;
}

/**
 * After signup or when the API returns EMAIL_NOT_VERIFIED.
 * Verification is only via the link in the email (?token=); this page explains that and runs the exchange when the link is opened.
 */
export const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tokenFromQuery = searchParams.get('token')?.trim() || '';
  const verificationEmailFailed =
    (location.state as { verificationEmailSent?: boolean } | null)?.verificationEmailSent === false;

  const [status, setStatus] = useState<'idle' | 'verifying' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);

  const userJson = localStorage.getItem('user');
  const user = useMemo(() => {
    if (!userJson) return null;
    try {
      return JSON.parse(userJson) as { emailVerified?: boolean; isAdmin?: boolean; email?: string };
    } catch {
      return null;
    }
  }, [userJson]);

  // Captured once so a successful verify in this visit does not also take the already-verified path.
  const alreadyVerifiedOnLoad = useRef(isUserEffectivelyVerified(user));
  const redirected = useRef(false);

  useEffect(() => {
    if (!alreadyVerifiedOnLoad.current || redirected.current) return;
    redirected.current = true;
    navigate(resolvePostVerifyPath(searchParams), { replace: true });
  }, [navigate, searchParams]);

  useEffect(() => {
    if (!user) return;
    if (alreadyVerifiedOnLoad.current) return;
    // When ?token= is present, the token effect below handles verify + one redirect.
    if (tokenFromQuery) return;
    if (isUserEffectivelyVerified(user)) {
      navigate(resolvePostVerifyPath(new URLSearchParams(window.location.search)), {
        replace: true,
      });
    }
  }, [user, navigate, tokenFromQuery]);

  useEffect(() => {
    if (!tokenFromQuery || alreadyVerifiedOnLoad.current) return;

    let cancelled = false;
    (async () => {
      setStatus('verifying');
      setMessage(null);
      try {
        // Deduped in authApi so React 18 Strict Mode does not send two POSTs and invalidate the one-time token.
        await authApi.verifyEmailWithToken(tokenFromQuery);
        if (cancelled) return;
        setStatus('success');
        setMessage('Your email is verified. Redirecting…');
        const to = resolvePostVerifyPath(new URLSearchParams(window.location.search));
        if (cancelled) return;
        setTimeout(() => navigate(to, { replace: true }), 1000);
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
        if (e instanceof ApiError) {
          setMessage(e.message);
        } else {
          setMessage(
            'This link is invalid or has expired. Use “Resend verification email” below, then click the new link in your inbox.'
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tokenFromQuery, navigate]);

  const handleBackToSignIn = () => {
    try {
      sessionStorage.removeItem(AFTER_EMAIL_VERIFY_KEY);
    } catch {
      /* ignore */
    }
    authApi.logout();
    navigate('/login', { replace: true });
  };

  const handleResend = async () => {
    if (!localStorage.getItem('authToken')) {
      setMessage(
        'Sign in on this browser, then tap Resend — or open the latest email and click the link there.'
      );
      return;
    }
    setResendLoading(true);
    setMessage(null);
    try {
      const res = await authApi.resendVerification();
      setMessage(res.message);
    } catch (e) {
      if (e instanceof ApiError) {
        setMessage(e.message);
      } else {
        setMessage('Could not send email. Try again later.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-700">Your email is verified. Redirecting…</p>
      </div>
    );
  }

  if (user && isUserEffectivelyVerified(user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-700">Redirecting…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow">
        <div>
          <h1 className="text-center text-2xl font-bold text-gray-900">Verify your email</h1>
          {verificationEmailFailed ? (
            <p className="mt-3 text-center text-sm text-gray-600">
              We created your account, but the verification email could not be sent. Use{' '}
              <strong className="text-gray-800">Resend verification email</strong> below.
            </p>
          ) : (
            <p className="mt-3 text-center text-sm text-gray-600">
              We sent a message to {user?.email ? <strong>{user.email}</strong> : 'your inbox'}.{' '}
              <strong className="text-gray-800">
                Open that email and click the verification link
              </strong>{' '}
              to finish setting up your account. If you don’t see it, check your spam folder.
            </p>
          )}
        </div>

        {tokenFromQuery && status === 'verifying' && (
          <p className="text-center text-indigo-600">Confirming your email…</p>
        )}

        {message && <p className="text-sm text-center text-gray-700">{message}</p>}

        {status === 'error' && (
          <p className="text-center text-sm text-gray-600">
            You can request a new email below and use the link from that message.
          </p>
        )}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={resendLoading}
            className={cn(
              'w-full py-2 px-4 rounded-md border text-sm font-medium',
              'border-indigo-200 text-indigo-700 hover:bg-indigo-50',
              resendLoading && 'opacity-50'
            )}
          >
            {resendLoading ? 'Sending…' : 'Resend verification email'}
          </button>
          <Link to="/account" className="text-center text-sm text-gray-500 hover:text-gray-700">
            Account & subscription
          </Link>
          <button
            type="button"
            onClick={handleBackToSignIn}
            className="text-center text-sm text-indigo-600 hover:text-indigo-800"
          >
            Back to sign in
          </button>
        </div>
      </div>
    </div>
  );
};
